import React, { useEffect, useState } from 'react';
import { FileText, Link2, CheckCircle2, Play, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { errorMessage, formatINR, formatDate } from '@/lib/utils';
import { openInvoice } from '@/pages/admin/AdminBilling';

// Super admin: platform-fee billing across every venue — sub-merchant and
// AutoPay status, what's accrued but not yet invoiced, and every invoice.
// The scheduler runs the jobs automatically (invoices on the 1st, debits
// from the 3rd); the "Run" buttons are for catching up or testing.
const MODE_LABEL = {
  partner: 'Partner mode — customers pay venues directly',
  direct: 'Direct mode — customer payments go to your own Razorpay account (partner keys not configured yet)',
  mock: 'Demo mode — no Razorpay keys configured',
};

const INVOICE_TONE = { paid: 'success', debit_failed: 'danger', payment_link_sent: 'warning' };

export default function AdminInvoices() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const { toast } = useToast();

  const load = async () => {
    try {
      const { data: d } = await api.get('/admin/platform/billing');
      setData(d);
    } catch (err) {
      toast({ title: 'Failed to load billing', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const run = async (key, path, describe) => {
    setBusy(key);
    try {
      const { data: result } = await api.post(path);
      toast({ title: 'Done', description: describe(result), variant: 'success' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    } finally {
      setBusy('');
    }
  };

  const markPaid = (inv) => {
    const note = window.prompt(`Mark ${inv.number} (${formatINR(inv.total)}) as paid. Note (e.g. "bank transfer UTR …"):`, '');
    if (note === null) return;
    setBusy(inv.id);
    api.post(`/admin/platform/billing/invoices/${inv.id}/mark-paid`, { note })
      .then(() => { toast({ title: 'Invoice marked paid', variant: 'success' }); load(); })
      .catch((err) => toast({ title: 'Failed', description: errorMessage(err.response?.data?.detail), variant: 'error' }))
      .finally(() => setBusy(''));
  };

  const sendLink = (inv) => run(inv.id, `/admin/platform/billing/invoices/${inv.id}/payment-link`, () => 'Payment link sent to the venue');

  if (!data) return <div className="text-white/40 text-sm py-12 text-center">Loading billing…</div>;

  return (
    <div className="space-y-8" data-testid="admin-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Platform</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Billing</h1>
          <p className="mt-2 text-xs text-white/50">{MODE_LABEL[data.checkout_mode]}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => run('inv', '/admin/platform/billing/run-invoices', (r) => `${r.created} invoice(s) created for last month`)}>
            <Play size={12} /> Generate invoices
          </Button>
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => run('debit', '/admin/platform/billing/run-debits', (r) => `${Object.keys(r.results || {}).length} invoice(s) processed`)}>
            <Play size={12} /> Run debits
          </Button>
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => run('overdue', '/admin/platform/billing/run-overdue', (r) => `${r.paused_academies.length} venue(s) paused`)}>
            <Play size={12} /> Check overdue
          </Button>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw size={12} /></Button>
        </div>
      </div>

      <div className="bg-surface-raised border border-white/10 p-6">
        <h3 className="font-heading font-bold text-xl uppercase mb-4">Venues</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40 border-b border-white/10">
                <th className="pb-3 pr-4">Venue</th>
                <th className="pb-3 pr-4">Razorpay</th>
                <th className="pb-3 pr-4">AutoPay</th>
                <th className="pb-3 pr-4">Booking fee</th>
                <th className="pb-3 pr-4">Not invoiced</th>
                <th className="pb-3">Online payments</th>
              </tr>
            </thead>
            <tbody>
              {data.venues.map((v) => (
                <tr key={v.academy_id} className="border-b border-white/5">
                  <td className="py-3 pr-4 font-medium">{v.academy_name}</td>
                  <td className="py-3 pr-4">
                    {v.razorpay_account_id
                      ? <Badge tone={v.account_live ? 'success' : 'warning'}>{(v.razorpay_account_status || 'created').replace(/_/g, ' ')}</Badge>
                      : <span className="text-white/40">Not linked</span>}
                  </td>
                  <td className="py-3 pr-4">
                    <Badge tone={v.mandate.mandate_status === 'active' ? 'success' : 'default'}>{(v.mandate.mandate_status || 'none').replace(/_/g, ' ')}</Badge>
                  </td>
                  <td className="py-3 pr-4">{v.booking_fee ? formatINR(v.booking_fee) : '—'}</td>
                  <td className="py-3 pr-4">{formatINR(v.unbilled.total)} <span className="text-white/40 text-xs">({v.unbilled.count})</span></td>
                  <td className="py-3">
                    {v.billing_paused ? (
                      <span className="inline-flex items-center gap-2">
                        <Badge tone="danger">Paused</Badge>
                        <Button variant="outline" size="sm" onClick={() => run(v.academy_id, `/admin/platform/billing/academies/${v.academy_id}/resume`, () => 'Venue resumed')}>Resume</Button>
                      </span>
                    ) : v.online_payments.ready ? <Badge tone="success">On</Badge> : <span className="text-white/50 text-xs">{v.online_payments.reason}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-surface-raised border border-white/10 p-6">
        <h3 className="font-heading font-bold text-xl uppercase mb-4">Invoices</h3>
        {data.invoices.length === 0 ? (
          <div className="text-white/40 text-sm">No invoices yet — they're created on the 1st for the previous month.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40 border-b border-white/10">
                  <th className="pb-3 pr-4">Invoice</th>
                  <th className="pb-3 pr-4">Venue</th>
                  <th className="pb-3 pr-4">Period</th>
                  <th className="pb-3 pr-4">Total</th>
                  <th className="pb-3 pr-4">Due</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3" />
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-white/5">
                    <td className="py-3 pr-4 font-mono text-xs">{inv.number}</td>
                    <td className="py-3 pr-4">{inv.academy_name}</td>
                    <td className="py-3 pr-4">{inv.period}</td>
                    <td className="py-3 pr-4">{formatINR(inv.total)}</td>
                    <td className="py-3 pr-4">{formatDate(inv.due_at)}</td>
                    <td className="py-3 pr-4">
                      <Badge tone={INVOICE_TONE[inv.status] || 'default'}>{inv.status.replace(/_/g, ' ')}</Badge>
                      {inv.overdue && inv.status !== 'paid' ? <Badge tone="danger" className="ml-2">Overdue</Badge> : null}
                    </td>
                    <td className="py-3 text-right whitespace-nowrap space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openInvoice(inv.id).catch(() => toast({ title: 'Could not open invoice', variant: 'error' }))}>
                        <FileText size={12} />
                      </Button>
                      {inv.status !== 'paid' && (
                        <>
                          <Button variant="outline" size="sm" disabled={busy === inv.id} onClick={() => sendLink(inv)} title="Send payment link">
                            <Link2 size={12} />
                          </Button>
                          <Button variant="outline" size="sm" disabled={busy === inv.id} onClick={() => markPaid(inv)} title="Mark paid">
                            <CheckCircle2 size={12} />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
